import { Join, type AsyncSequence } from "@pdfking/tfunc"

/**
 * Creates ReadableStream from async iterable of byte arrays.
 *
 * Converts AsyncSequence<Uint8Array> to Web Streams API ReadableStream.
 *
 * @param body - Async sequence of Uint8Array chunks
 * @param cancel - Optional cancellation callback
 * @returns ReadableStream for use with Response or other stream consumers
 *
 * @example
 * ```ts
 * import { StreamCreate } from "@pdfking/io"
 *
 * const stream = StreamCreate(async function*() {
 *   yield new Uint8Array([1, 2, 3])
 * }())
 * const response = new Response(stream)
 * ```
 */
export const StreamCreate = (
  body: AsyncSequence<Uint8Array>,
  cancel?: (reason?: unknown) => void
): ReadableStream => {
  // One iterator out of either side of AsyncSequence — and generators run
  // nothing until asked, which keeps the body untouched below.
  const chunks = Join(body)

  return new ReadableStream(
    {
      // A reader that stops early stops the generator too: whatever it holds
      // open — a file handle, a lock — is released by its own `finally`, which
      // only runs if somebody returns it.
      async cancel(reason) {
        await chunks.return(undefined)
        cancel?.(reason)
      },
      // Pulled rather than pumped from `start`: nothing is drained until
      // somebody reads the stream, so a Response built over this costs nothing
      // for the caller who only wanted the headers.
      async pull(controller) {
        const { done, value } = await chunks.next()
        if (done) controller.close()
        else controller.enqueue(value)
      },
    },
    // A stream that wants nothing in hand is a stream that pulls nothing until
    // it is read: the default is to fill a chunk on construction, which would
    // open a file for every response nobody ever reads.
    { highWaterMark: 0 }
  )
}
